import React, { useEffect, useState } from "react";
import * as api from "../../../../api/generated-api";
import { Box, Typography, useTheme } from "@mui/material";
import { clickToLinkHandler } from "../../../../helpers/utils";
import DKMidDot from "../../../common/DKMidDot";

type SignatureItemPropTypes = {
  orgId: number;
  dealId: number;
  isUserCompanyMember: boolean;
  dealLegalEntity: api.DealLegalEntity;
  dealDocumentSignatures: api.DealDocumentSignature[];
  isSelected: boolean;
  rightComponent?: React.ReactNode;
  onOpenSignatureRequestPanel: () => void;
};

const SignatureStatus = ({
  orgId,
  dealId,
  isUserCompanyMember,
  dealLegalEntity,
  dealDocumentSignatures,
  isSelected: selected,
  rightComponent,
  onOpenSignatureRequestPanel,
}: SignatureItemPropTypes) => {
  const [signatureStatus, setSignatureStatus] = useState<{
    [api.SigningStatus.notRequested]: number;
    [api.SigningStatus.requested]: number;
    [api.SigningStatus.signed]: number;
  }>({
    [api.SigningStatus.notRequested]: 0,
    [api.SigningStatus.requested]: 0,
    [api.SigningStatus.signed]: 0,
  });

  const theme = useTheme();

  useEffect(() => {
    const signaturesByStatus = dealDocumentSignatures.reduce(
      (accumulator, currentValue) => {
        if (accumulator[currentValue.status]) {
          accumulator[currentValue.status] += 1;
        } else {
          accumulator[currentValue.status] = 1;
        }

        return accumulator;
      },
      {
        [api.SigningStatus.notRequested]: 0,
        [api.SigningStatus.requested]: 0,
        [api.SigningStatus.signed]: 0,
      }
    );

    setSignatureStatus(signaturesByStatus);
  }, [dealDocumentSignatures]);

  const totalSignatures =
    signatureStatus.notRequested +
    signatureStatus.requested +
    signatureStatus.signed;

  const signaturesNotRequestedDegs =
    (signatureStatus.notRequested / totalSignatures) * 360;
  const signaturesRequestedDegs =
    (signatureStatus.requested / totalSignatures) * 360;

  const firstBreakDegs = signaturesNotRequestedDegs;
  const secondBreakDegs = signaturesNotRequestedDegs + signaturesRequestedDegs;

  let label = dealLegalEntity.name;

  if (
    dealLegalEntity.legalEntityType ===
    api.LawFirmLegalEntityType.Company_Counsel
  ) {
    label = `${dealLegalEntity.person.firstName} ${dealLegalEntity.person.lastName}`;
  }

  if (
    dealLegalEntity.legalEntityType ===
    api.CompanyLegalEntityType.Chief_Executive_Officer
  ) {
    label = `${label} (CEO)`;
  }

  if (
    dealLegalEntity.legalEntityType === api.CompanyLegalEntityType.Secretary
  ) {
    label = `${label} (Secretary)`;
  }

  return (
    <Box
      key={dealLegalEntity.id}
      marginTop={1}
      marginBottom={3}
      data-testid="signature-group-signer-row"
      display={"flex"}
      flex={1}
      flexDirection={"row"}
    >
      <Box
        display={"flex"}
        flex={1}
        marginLeft={2}
        marginRight={5}
        data-testid="signature-status"
      >
        <Box
          marginRight={2}
          sx={{
            width: "26px",
            height: "26px",
          }}
        >
          <Box
            style={{
              position: "relative",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              backgroundImage: `conic-gradient(${theme.palette.tertiary.light} 0deg ${firstBreakDegs}deg, ${theme.palette.secondary.dark} ${firstBreakDegs}deg ${secondBreakDegs}deg, ${theme.palette.primary.dark} ${secondBreakDegs}deg)`,
              zIndex: 0,
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                backgroundColor: "white",
              }}
            />
          </Box>
        </Box>
        <Box>
          <Typography>{label}</Typography>
          {dealDocumentSignatures.length === 0 && (
            <Box>
              {isUserCompanyMember && (
                <Box
                  onClick={() => {
                    clickToLinkHandler(`/${orgId}/deals/${dealId}/documents`);
                  }}
                >
                  <Typography variant="tableLink">
                    Configure Documents to Sign
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {!selected && dealDocumentSignatures.length > 0 && (
            <Box
              data-testid="request-signatures-link"
              onClick={onOpenSignatureRequestPanel}
              display={"flex"}
            >
              <Typography variant="tableLink">View Details</Typography>
              <DKMidDot color={theme.palette.secondary.darker} />
              <Typography variant="tableLink">{`${signatureStatus.signed} of ${totalSignatures} signed`}</Typography>

              {signatureStatus.notRequested > 0 && (
                <>
                  <DKMidDot color={theme.palette.secondary.darker} />
                  <Typography variant="tableLink">{`${signatureStatus.notRequested} to request`}</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box display={"flex"} flex={1}>
        {rightComponent && rightComponent}
      </Box>
    </Box>
  );
};

export default SignatureStatus;
